import { useEffect, useState } from "react";
import type { FormEvent } from "react";
import { KeyRound, UserPlus } from "lucide-react";
import { api, ApiError } from "../../api/client";
import { Button } from "../../components/Button";
import { Dialog } from "../../components/Dialog";
import { Input } from "../../components/Input";
import { Select } from "../../components/Select";
import { useToast } from "../../components/Toast";
import { PASSWORD_HINT, passwordProblem } from "../auth/ChangePasswordDialog";
import { ROLE_OPTIONS } from "./UserList";
import type { AdminUser } from "./UserList";
import type { UserRole } from "../../api/types";

// 新增帳號 / 代設密碼。兩件事共用同一個對話框,因為它們是同一件事的兩面:
// **替一個帳號決定一組密碼**,而且必須遵守同一組規矩。
//
// 三條規矩,與 ChangePasswordDialog 同一條線:
// 1. **兩次密碼不一致就不送出。** 送出去的話後端會照收,同事從此得用打錯的
//    那一個登入,而老闆以為自己設的是另一個 —— 第一次就進不來。
// 2. **失敗絕不關閉對話框。** 關掉等於把剛打的每一欄丟掉,只能重打一次。
// 3. **密碼明文只出現在送出的 body 裡。** 不進錯誤訊息、不進成功提示、不進畫面。

// 角色選項與使用者清單共用同一份(`UserList.tsx` 的 `ROLE_OPTIONS`)——
// 兩邊各寫一份的下場第十二輪已經發生過:加了財務角色,兩支下拉都沒補上,
// 老闆在畫面上根本指派不了財務。

export interface UserDialogProps {
  /** create:開一組新帳號;reset:替既有帳號代設密碼。 */
  mode: "create" | "reset";
  open: boolean;
  /** reset 模式的對象。 */
  user?: AdminUser | null;
  onClose: () => void;
  /** 成功之後讓清單重新載入。 */
  onSaved?: () => void;
}

export function UserDialog({ mode, open, user = null, onClose, onSaved }: UserDialogProps) {
  const { push } = useToast();
  const [email, setEmail] = useState("");
  const [name, setName] = useState("");
  const [phone, setPhone] = useState("");
  const [role, setRole] = useState<UserRole>("STAFF");
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  const creating = mode === "create";
  const target = user;

  // 每次開啟都從空白開始。上一次打的密碼沒有理由留在記憶體裡等下一次。
  useEffect(() => {
    if (!open) return;
    setEmail("");
    setName("");
    setPhone("");
    setRole("STAFF");
    setPassword("");
    setConfirm("");
    setError(null);
    setSubmitting(false);
  }, [open]);

  const handleSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (submitting) return;

    if (creating && email.trim() === "") {
      setError("請填 Email,它就是登入帳號");
      return;
    }
    const problem = passwordProblem(password);
    if (problem !== null) {
      // 訊息只講規則,不回吐使用者打了什麼。
      setError(problem);
      return;
    }
    if (password !== confirm) {
      setError(creating ? "兩次輸入的密碼不一致" : "兩次輸入的新密碼不一致");
      return;
    }

    setSubmitting(true);
    setError(null);
    try {
      if (creating) {
        await api.post<AdminUser>("/users", {
          email: email.trim(),
          name: name.trim(),
          phone: phone.trim(),
          role,
          password,
        });
        // 提示裡帶的是 Email,不是密碼。
        push(`已建立 ${email.trim()} 的帳號`, "success");
      } else {
        if (target === null) return;
        await api.post<void>(`/users/${target.id}/reset-password`, { new_password: password });
        push(`已替 ${target.name || target.email} 設定新密碼`, "success");
      }
      onSaved?.();
      onClose();
    } catch (err) {
      // 後端的原句照抄(例如「已經有帳號了」),前端不自己補細節。
      setError(err instanceof ApiError ? err.message : "無法連線到伺服器,請稍後再試。");
      setSubmitting(false);
    }
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      title={creating ? "新增帳號" : `重設 ${target?.name || target?.email || ""} 的密碼`}
      maxWidth="max-w-sm"
      // 打到一半誤點背景就整份消失太傷,關閉一律走 Esc 或明確的取消 / 關閉鈕。
      closeOnBackdrop={false}
    >
      <form onSubmit={handleSubmit} className="flex flex-col gap-3">
        {creating ? (
          <>
            <Input
              label="Email"
              type="email"
              name="email"
              autoComplete="off"
              autoFocus
              hint="這就是登入帳號,建立後不能改"
              value={email}
              onChange={(event) => setEmail(event.target.value)}
            />
            <Input
              label="姓名"
              name="name"
              autoComplete="off"
              value={name}
              onChange={(event) => setName(event.target.value)}
            />
            <Input
              label="電話"
              name="phone"
              autoComplete="off"
              value={phone}
              onChange={(event) => setPhone(event.target.value)}
            />
            <Select
              label="職稱"
              name="role"
              value={role}
              options={ROLE_OPTIONS}
              onChange={(event) => setRole(event.target.value as UserRole)}
            />
          </>
        ) : (
          <p className="m-0 text-sm text-[var(--text-2)]">
            不需要對方目前的密碼。設好之後請當面或用電話告訴他,並請他登入後自行改掉。
          </p>
        )}

        <Input
          label={creating ? "密碼" : "新密碼"}
          type="password"
          name="new_password"
          autoComplete="new-password"
          autoFocus={!creating}
          hint={PASSWORD_HINT}
          value={password}
          onChange={(event) => setPassword(event.target.value)}
        />
        <Input
          label={creating ? "再輸入一次密碼" : "再輸入一次新密碼"}
          type="password"
          name="confirm_password"
          autoComplete="new-password"
          value={confirm}
          onChange={(event) => setConfirm(event.target.value)}
        />

        {error ? (
          <p
            role="alert"
            className="m-0 rounded-[var(--radius)] border border-[var(--danger)] bg-[var(--surface-2)] px-2.5 py-2 text-sm text-[var(--danger)]"
          >
            {error}
          </p>
        ) : null}

        <div className="mt-1 flex justify-end gap-2">
          <Button type="button" variant="secondary" onClick={onClose} disabled={submitting}>
            取消
          </Button>
          <Button
            type="submit"
            variant="primary"
            loading={submitting}
            icon={
              creating ? (
                <UserPlus aria-hidden="true" size={16} />
              ) : (
                <KeyRound aria-hidden="true" size={16} />
              )
            }
          >
            {creating ? "建立帳號" : "設定新密碼"}
          </Button>
        </div>
      </form>
    </Dialog>
  );
}
